import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { WhatsApp, EyeOpen } from './icons';
import { Product } from '@dentopharma/types';
import { getProductImage, productImageMap } from '../assets/products';
import { enquiryLink } from '../lib/enquiry';

interface ProductCardProps {
  product: Product;
}

const ProductCard = ({ product }: ProductCardProps) => {
  // Get product image - prioritize local images, then API media, then fallback
  const getProductImageUrl = () => {
    // First, try to find a local image based on product name/slug
    const productSlug = product.name.toLowerCase()
      .replace(/[^a-z0-9]/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '');

    // Special handling for OptraGate products
    if (productSlug.includes('optragate') || product.name.toLowerCase().includes('optragate')) {
      return getProductImage('optragate');
    }

    // Special handling for ProXem products
    if (productSlug.includes('proxem') || product.name.toLowerCase().includes('proxem')) {
      return getProductImage('proxem');
    }

    // Special handling for Cention products
    if (productSlug.includes('cention') || product.name.toLowerCase().includes('cention')) {
      return getProductImage('cention-n');
    }

    // Check if we have a mapped local image for this product
    for (const [key, value] of Object.entries(productImageMap)) {
      if (productSlug.includes(key) || product.name.toLowerCase().includes(key)) {
        return getProductImage(value);
      }
    }

    // If no local image found, try API media (for manually added images)
    if (product.media && product.media.length > 0) {
      return product.media[0].url;
    }

    // Default fallback
    return getProductImage('default');
  };

  const openEnquiry = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    window.open(enquiryLink(product.name), '_blank', 'noopener');
  };

  const category = (product as any).category?.name as string | undefined;

  return (
    <motion.div
      whileHover={{ y: -5 }}
      transition={{ duration: 0.2 }}
      className="group h-full"
    >
      <Link to={`/product/${product.id}`} className="block h-full">
        <div className="card overflow-hidden h-full flex flex-col">
          {/* Product Image */}
          <div className="relative aspect-square bg-gray-100 rounded-2xl overflow-hidden mb-4">
            <img
              src={getProductImageUrl()}
              alt={product.name}
              loading="lazy"
              decoding="async"
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
              onError={(e) => {
                // Fallback to default image if the main image fails to load
                const target = e.target as HTMLImageElement;
                target.src = getProductImage('default');
              }}
            />

            {/* Quick Actions */}
            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all duration-300 flex items-center justify-center opacity-0 group-hover:opacity-100">
              <div className="flex space-x-2">
                <button
                  onClick={openEnquiry}
                  className="w-10 h-10 bg-white rounded-full flex items-center justify-center text-[#25D366] hover:bg-[#25D366] hover:text-white transition-colors duration-200"
                  title="Enquire on WhatsApp"
                >
                  <WhatsApp size={18} />
                </button>
                <Link
                  to={`/product/${product.id}`}
                  className="w-10 h-10 bg-white rounded-full flex items-center justify-center text-[#0D56A6] hover:bg-[#0D56A6] hover:text-white transition-colors duration-200"
                  title="View Details"
                >
                  <EyeOpen size={18} />
                </Link>
              </div>
            </div>

            {/* Category chip — real, varies per product */}
            {category && (
              <div className="absolute top-3 left-3 bg-white/90 backdrop-blur-sm text-gray-700 px-2.5 py-1 rounded-full text-[11px] font-medium shadow-sm">
                {category}
              </div>
            )}
          </div>

          {/* Product Info — flex column so the button aligns to the bottom
              and every card in a row is the same height */}
          <div className="flex flex-col flex-1">
            <div className="space-y-2 mb-4">
              {/* Manufacturer Name */}
              {product.manufacturer && (
                <div className="text-sm text-primary-600 font-medium">
                  {product.manufacturer.name}
                </div>
              )}

              {/* Product Name */}
              <h3 className="font-semibold text-gray-900 group-hover:text-primary-600 transition-colors duration-200 line-clamp-2">
                {product.name}
              </h3>

              {/* Description */}
              {product.shortDescription && (
                <p className="text-sm text-gray-600 line-clamp-2">
                  {product.shortDescription}
                </p>
              )}

              {/* Options / availability signal (no price) */}
              {product.variants && product.variants.length > 1 && (
                <div className="text-xs text-gray-400">{product.variants.length} options available</div>
              )}
            </div>

            {/* Enquire Button pinned to the bottom */}
            <button
              onClick={openEnquiry}
              className="w-full inline-flex items-center justify-center gap-2 font-semibold py-2.5 px-4 rounded-xl transition-all duration-300 text-white bg-[#0D56A6] hover:bg-[#0A4A8F] mt-auto"
            >
              <WhatsApp size={16} />
              Enquire
            </button>
          </div>
        </div>
      </Link>
    </motion.div>
  );
};

export default ProductCard;
